import benchmarks from 'api/benchmarks';
import ConfirmModal from 'components/ConfirmModal/ConfirmModal';
import { Input } from 'components/form/Input/Input';
import { showSuccess } from 'helpers/showSuccess';
import { ChangeEvent, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { routes } from 'routes';
import { AbsoluteReturn } from 'types/AbsoluteReturn';
import { AssetWeighted } from 'types/AssetWeighted';
import { BenchmarkTypes } from 'types/Benchmark';
import { CompositeReturn } from 'types/CompositeReturn';
import { CompositeWeighted } from 'types/CompositeWeighted';
import { PullForward } from 'types/PullForward';
import { StaticWeighted } from 'types/StaticWeighted';
import { ProblemDetails } from 'helpers/problemDetails';
import { BenchmarkNameMaxLength } from 'modules/Benchmarks/BenchmarkList/utils';
import { Benchmark, General } from 'api/errorCodes';

type Properties = {
  benchmark:
    | PullForward
    | AbsoluteReturn
    | CompositeReturn
    | AssetWeighted
    | CompositeWeighted
    | StaticWeighted;
  visible: boolean;
  onClose: () => void;
};

export const CloneModal = ({ benchmark, visible, onClose }: Properties) => {
  const [newName, setNewName] = useState('');
  const [cloneError, setCloneError] = useState('');
  const history = useHistory();
  const { mutate, isSuccess, error, data, isLoading }: any =
    benchmarks.SaveBenchmarks();

  useEffect(() => {
    if (error?.response) {
      const apiError = new ProblemDetails(error.response.data);
      var messageOverride;
      apiError.errors?.forEach((error) => {
        if (error.code === General.RequiredStringIsMissing) {
          messageOverride = 'Benchmark name cannot be empty';
          return;
        } else if (error.code === Benchmark.NameInUse) {
          messageOverride = 'Benchmark name already exists.';
          return;
        } else {
          messageOverride = error.message;
        }
      });

      setCloneError(messageOverride ?? apiError.detail);
    }
  }, [error]);

  const getBody = (benchmark: any) => {
    const data: any = {
      [BenchmarkTypes.AbsoluteReturn]: {
        ...benchmark,
        numberOfDaysInYearId: benchmark?.numberOfDaysInYearId,
      },
      [BenchmarkTypes.StaticWeighted]: {
        ...benchmark,
        rebalanceFrequencyId: benchmark?.rebalanceFrequency?.typeId,
        rebalanceFrequencyCustomMonths:
          benchmark?.rebalanceFrequency?.customMonths,
      },
      [BenchmarkTypes.CompositeReturn]: {
        ...benchmark,
        performanceVariationCode: benchmark?.performanceVariationCode,
      },
      [BenchmarkTypes.PullForward]: benchmark,
      [BenchmarkTypes.AssetWeighted]: {
        ...benchmark,
        weightingMethodologyId: benchmark?.weightingMethodology?.id,
        performanceVariationCode: benchmark?.performanceVariationCode,
      },
      [BenchmarkTypes.CompositeWeighted]: {
        ...benchmark,
        weightingMethodologyId: benchmark?.weightingMethodology?.id,
        performanceVariationCode: benchmark?.performanceVariationCode,
      },
    };

    if (benchmark.type?.id) {
      return data[benchmark.type?.id];
    }
  };

  const handleClone = () => {
    if (benchmark) {
      const requestObject = {
        ...getBody(benchmark),
        calculationFrequencyId: benchmark?.calculationFrequency?.id,
        typeId: benchmark.type?.id,
        name: newName,
      };

      if (requestObject.id) {
        delete requestObject.id;
      }

      mutate({
        body: requestObject,
      });
    }
  };

  useEffect(() => {
    if (isSuccess) {
      onClose();

      history.push(
        routes.getBenchmarkDetails(`${benchmark.type?.id}`, data.data.id)
      );

      showSuccess('Benchmark was successfully cloned');
    }
  }, [isSuccess]);

  const handleClose = () => {
    setNewName('');
    setCloneError('');
    onClose();
  };

  return (
    <ConfirmModal
      onClose={handleClose}
      onConfirm={handleClone}
      show={visible}
      title='Clone'
      isCentered={true}
      confirmLabel='Save'
      testid='cloneBenchmarkModal'
      size='md'
      isLoading={isLoading}
    >
      <Input
        label='Benchmark Name'
        value={newName}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          setNewName(e.target.value)
        }
        error={cloneError}
        errorMessage={cloneError}
        maxLength={BenchmarkNameMaxLength}
      />
    </ConfirmModal>
  );
};
